import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';

import { KEYBOARD_KEYS } from './constants';

export const useSplittedInput = () => {
	const inputRefs = useRef<Array<HTMLInputElement | null>>(new Array(4).fill(null));
	const containerRef = useRef<HTMLDivElement>(null);

	const [focusedInputs, setFocusedInputs] = useState<boolean[]>(() => new Array(4).fill(false));

	const updateOnFocus = (focusedIndex: number) => {
		const currentInput = inputRefs.current[focusedIndex];

		const inputs = inputRefs.current as HTMLInputElement[];

		// todo findLastIndex polyfill - lodash?
		let firstNotEmptyIndex = -1;
		for (let i = inputs.length - 1; i >= 0; i--) {
			if (inputs[i]?.value !== '') {
				firstNotEmptyIndex = i;
				break;
			}
		}

		const realIndexToFocus =
			focusedIndex > firstNotEmptyIndex + 1 ? firstNotEmptyIndex + 1 : focusedIndex;

		if (realIndexToFocus !== focusedIndex) {
			inputRefs.current[realIndexToFocus]?.focus();
			inputRefs.current[focusedIndex]?.blur();
		}

		setFocusedInputs(prev => prev.map((el, index) => (index === realIndexToFocus ? true : el)));

		if (!currentInput) {
			return;
		}

		currentInput.selectionStart = 0;
		currentInput.selectionEnd = 0;
	};

	const updateOnBlur = (bluredIndex: number) => {
		setFocusedInputs(prev => prev.map((el, index) => (index === bluredIndex ? false : el)));
	};

	const updatePositionOnChange = (
		event: ChangeEvent<HTMLInputElement>,
		_value: string | number | undefined,
		index: number,
	) => {
		const nextInputIndex = event.target.value === '' ? index - 1 : index + 1;
		const selectionIndex = event.target.value === '' ? 1 : 0;

		if (index !== 0) {
			inputRefs.current[index]?.blur();
		}

		const nextInput = inputRefs.current[nextInputIndex];

		if (!nextInput) {
			return;
		}

		nextInput.focus();
		nextInput.selectionEnd = selectionIndex;
		nextInput.selectionStart = selectionIndex;
	};

	const updatePositionOnKeydown = (event: KeyboardEvent<HTMLInputElement>, index: number) => {
		const currentInput = inputRefs.current[index];

		if (!currentInput) {
			return;
		}

		// если инпут в фокусе, но в нём уже есть цифра и каретка стоит в конце инпута
		if (
			event.key >= '0' &&
			event.key <= '9' &&
			currentInput.value &&
			(currentInput.selectionEnd === 1 || currentInput.selectionStart === 1)
		) {
			currentInput.value = event.key;
		}

		if (event.key === KEYBOARD_KEYS.BACKSPACE && index === 0) {
			currentInput.value = '';
		}

		if (event.key === KEYBOARD_KEYS.DELETE) {
			currentInput.selectionEnd = currentInput.selectionStart = 0;
		}

		const isLeftDirection =
			event.key === KEYBOARD_KEYS.BACKSPACE || event.key === KEYBOARD_KEYS.ARROW_LEFT;

		if (isLeftDirection && index > 0) {
			const isNeedToMoveLeft =
				currentInput.value === '' ||
				((currentInput.selectionEnd ?? 0) === 0 &&
					(currentInput.selectionStart ?? 0) === 0);

			if (!isNeedToMoveLeft) {
				return;
			}

			// если нажали бекспейс сразу после ввода цифры - стираем предыдущий инпут
			const isNeedToClearPrev =
				currentInput.value === '' && event.key === KEYBOARD_KEYS.BACKSPACE;
			const isNeedToClearCurrent =
				currentInput.value !== '' && event.key === KEYBOARD_KEYS.BACKSPACE;

			if (isNeedToClearCurrent) {
				currentInput.selectionStart = 1;
				currentInput.selectionEnd = 1;
			}

			setTimeout(() => {
				currentInput.blur();

				const prevInput = inputRefs.current[index - 1];

				if (!prevInput) {
					return;
				}

				prevInput.focus();
				prevInput.selectionEnd = 1;
				prevInput.selectionStart = 1;

				if (isNeedToClearPrev) {
					prevInput.value = '';
				}
			}, 0);
		}

		const isRightDirection = event.key === KEYBOARD_KEYS.ARROW_RIGHT;

		if (isRightDirection) {
			const isNeedToMoveRight =
				((currentInput.selectionEnd ?? 0) > 0 && (currentInput.selectionStart ?? 0) > 0) ||
				currentInput.value === '';

			if (!isNeedToMoveRight) {
				return;
			}

			// is last input focused
			if (index >= inputRefs.current.length - 1) {
				return;
			}

			setTimeout(() => {
				currentInput.blur();

				const nextInput = inputRefs.current[index + 1];

				if (nextInput) {
					nextInput.focus();
					nextInput.selectionEnd = 0;
					nextInput.selectionStart = 0;
				}
			}, 0);
		}
	};

	// todo невозможно нормально использовать рефы с текущим MaskedInputом - поэтому рефы набиваем в эффекте
	useEffect(() => {
		if (!containerRef.current) {
			return;
		}

		const inputs = containerRef.current.querySelectorAll('input');
		inputRefs.current = inputRefs.current.map((_, index) => inputs[index]);
	}, [containerRef, inputRefs]);

	return {
		inputRefs,
		containerRef,
		updatePositionOnChange,
		updatePositionOnKeydown,
		updateOnFocus,
		updateOnBlur,
		focusedInputs,
	};
};
